import { useEffect, useRef, useState } from 'react';

/**
 * Hook para prevenir que a página entre em suspensão
 * Usa Wake Lock API se disponível no navegador, caso contrário, usa fallbacks
 */

const useWakeLock = (shouldKeepAwake = false) => {
  const [isWakeLockActive, setIsWakeLockActive] = useState(false);
  const wakeLockRef = useRef(null);
  const fallbackIntervalRef = useRef(null);
  const fallbackVideoRef = useRef(null);

  const isWakeLockSupported = 'wakeLock' in navigator;

  const requestWakeLock = async () => {
    try {
      if (isWakeLockSupported) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        setIsWakeLockActive(true);

        console.log(
          'Wake Lock ativado ✅ - A página NÃO entrará em suspensão.'
        );

        wakeLockRef.current.addEventListener('release', () => {
          setIsWakeLockActive(false);
        });

        return true;
      } else {
        startFallbackKeepAwake();
        return true;
      }
    } catch (err) {
      console.warn('Erro ao ativar Wake Lock:', err);

      startFallbackKeepAwake();
      return false;
    }
  };

  const releaseWakeLock = async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        setIsWakeLockActive(false);
      }

      stopFallbackKeepAwake();

      console.log(
        'Wake Lock desativado ❌ - A página PODE entrar em suspensão.'
      );
    } catch (err) {
      console.warn('Erro ao desativar Wake Lock:', err);
    }
  };

  const startFallbackKeepAwake = () => {
    fallbackIntervalRef.current = setInterval(() => {
      const currentTitle = document.title;
      document.title = currentTitle;
    }, 30000);

    if (!fallbackVideoRef.current) {
      const video = document.createElement('video');
      video.src =
        'data:video/mp4;base64,AAAAIGZ0eXBtcDQyAAAAAG1wNDJpc29tYXZjMQAAAAhmdHlwbXA0MgAAAABtcDQy';
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.style.position = 'fixed';
      video.style.left = '-1px';
      video.style.top = '-1px';
      video.style.width = '1px';
      video.style.height = '1px';
      video.style.opacity = '0';
      video.style.pointerEvents = 'none';
      video.style.zIndex = '-1000';

      document.body.appendChild(video);
      fallbackVideoRef.current = video;
    }

    setIsWakeLockActive(true);

    console.log('✅ Fallback ativo - página NÃO entrará em suspensão.');
  };

  const stopFallbackKeepAwake = () => {
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current);
      fallbackIntervalRef.current = null;
    }

    if (fallbackVideoRef.current) {
      fallbackVideoRef.current.pause();
      document.body.removeChild(fallbackVideoRef.current);
      fallbackVideoRef.current = null;
    }

    if (!wakeLockRef.current) setIsWakeLockActive(false);

    console.log('❌ Fallback inativo - página PODE entrar em suspensão.');
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        shouldKeepAwake &&
        document.visibilityState === 'visible' &&
        !isWakeLockActive
      ) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [shouldKeepAwake, isWakeLockActive]);

  useEffect(() => {
    if (shouldKeepAwake) requestWakeLock();
    else releaseWakeLock();

    return () => {
      releaseWakeLock();
    };
  }, [shouldKeepAwake]);

  return {
    isWakeLockSupported,
    isWakeLockActive,
    requestWakeLock,
    releaseWakeLock
  };
};

export default useWakeLock;
